import React from 'react';
import { Typography, Card as AntCard, Button } from 'antd';
import { Calendar, User, ArrowRight, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

// Type workaround for Card component
const Card = AntCard as any;

const { Title, Text, Paragraph } = Typography;

export const Blogs: React.FC = () => {
  const placeholderBlogs = [
    {
      title: 'The Future of Smart Farming in India',
      excerpt: 'Exploring how IoT and AI technologies are transforming traditional agriculture practices across Indian farms.',
      date: 'Coming Soon',
      author: 'QodeAris Team',
      category: 'Technology',
    },
    {
      title: 'DhartiNetra Success Stories',
      excerpt: 'Real-world case studies of farmers who increased their yields and reduced costs using DhartiNetra.',
      date: 'Coming Soon',
      author: 'QodeAris Team',
      category: 'Case Studies',
    },
    {
      title: 'Understanding Soil Health Monitoring',
      excerpt: 'A comprehensive guide to NPK levels, soil moisture, and how to interpret sensor data for better crop management.',
      date: 'Coming Soon',
      author: 'QodeAris Team',
      category: 'Education',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Helmet>
        <title>QodeAris Blogs - Insights on Smart Farming</title>
        <meta name="description" content="Read the latest insights, success stories, and educational content about smart farming and agricultural technology in India." />
        <meta name="keywords" content="agritech blog, smart farming news, agriculture technology insights, qodearis blog" />
      </Helmet>
      <Navbar />
      <main id="main-content" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Title level={1} className="text-5xl md:text-6xl font-bold mb-6">
              QodeAris
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"> Blogs</span>
            </Title>
            <Paragraph className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Insights, stories, and knowledge about smart agriculture, technology, and sustainable farming practices.
            </Paragraph>
          </motion.div>

          {/* Coming Soon Notice */}
          <div className="mb-16 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-2xl p-12 text-center">
            <BookOpen className="w-20 h-20 mx-auto mb-6" aria-hidden="true" />
            <Title level={2} className="text-white mb-4">Blog Coming Soon</Title>
            <Paragraph className="text-xl text-white/90 max-w-2xl mx-auto">
              We're preparing valuable content about smart farming, agricultural technology, 
              and success stories from farmers using our solutions. Stay tuned!
            </Paragraph>
          </div>

          {/* Upcoming Topics */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">Upcoming Topics</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300">
                Here's what we'll be writing about
              </Text>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {placeholderBlogs.map((blog, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full shadow-lg hover:shadow-xl transition-shadow">
                    <div className="mb-4">
                      <span className="inline-block bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-sm font-semibold px-3 py-1 rounded-full">
                        {blog.category}
                      </span>
                    </div>
                    <Title level={4} className="mb-3">{blog.title}</Title>
                    <Paragraph className="text-gray-600 dark:text-gray-300 mb-4">
                      {blog.excerpt}
                    </Paragraph>
                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" aria-hidden="true" />
                        <span>{blog.date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" aria-hidden="true" />
                        <span>{blog.author}</span>
                      </div>
                    </div>
                    <Button
                      type="link"
                      className="p-0 h-auto text-green-600 hover:text-green-700"
                      icon={<ArrowRight className="w-4 h-4" aria-hidden="true" />}
                      disabled
                    >
                      Read More
                    </Button>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Newsletter Signup */}
          <section>
            <Card className="bg-white dark:bg-gray-800 shadow-xl">
              <div className="text-center py-8">
                <Title level={3} className="mb-4">Get Notified When We Publish</Title>
                <Paragraph className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-6">
                  Subscribe to our newsletter to receive updates about new blog posts, 
                  farming tips, and product announcements.
                </Paragraph>
                <a href="/contact">
                  <Button
                    type="primary"
                    size="large"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Contact Us to Subscribe
                  </Button>
                </a>
              </div>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};
